/* © CNES */
import * as gjv from 'geojson-validation';
import JSZip from 'jszip';
import KML from 'ol/format/KML';
import GeoJSON from 'ol/format/GeoJSON';
import shp from 'shpjs';
import { GeoJsonPolygon } from '../../../../../../models/GeoJsonPolygon';
/**
 * function that parse GeoJson file
 * @param file file to parse
 * @returns promise
 */
export const readGeoJsonFile = (file: File) => {
  return new Promise((resolve, reject) => {
    if (file.name.split('.').pop() !== 'geojson') {
      reject(
        new Error('Extension not allowed, please choose a .GeoJSON file.')
      );
    } else {
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        if (event.target) {
          try {
            const parsedData = JSON.parse(event.target.result as string);
            if (gjv.valid(parsedData)) {
              if (parsedData.features.length === 0) {
                reject(new Error('No geographical entity found.'));
              } else if (parsedData.features.length > 1) {
                reject(new Error('Several geographical areas defined.'));
              } else {
                resolve(parsedData);
              }
            } else reject(new Error('Not valid geoJson file'));
          } catch (error) {
            reject(error);
          }
        }
      };

      fileReader.onerror = (error) => reject(error);
      fileReader.readAsText(file);
    }
  });
};

/**
 * function that parse zip->SHP file
 * Bypasses shpjs.parseZip() which uses JSZip's 'nodebuffer' type – unsupported
 * in webpack 5 browser builds (global Buffer is not polyfilled).
 * Instead, uses JSZip directly with 'arraybuffer' and calls shpjs low-level parsers.
 * @param file file to parse
 * @returns
 */
export const readShapeFile = (file: File) => {
  return new Promise((resolve, reject) => {
    if (file.name.split('.').pop() !== 'zip') {
      reject(new Error('Extension not allowed, please choose a .ZIP file.'));
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      if (!e.target?.result) {
        reject(new Error('Error reading file.'));
        return;
      }
      try {
        const zip = new JSZip();
        await zip.loadAsync(e.target.result as ArrayBuffer);

        let shpBuffer: ArrayBuffer | null = null;
        let dbfBuffer: ArrayBuffer | null = null;
        let cpgBuffer: ArrayBuffer | null = null;
        let prjText: string | null = null;

        await Promise.all(
          zip.file(/.+/).map(async (f) => {
            if (f.name.includes('__MACOSX')) return;
            const ext = f.name.slice(-3).toLowerCase();
            if (ext === 'shp') shpBuffer = await f.async('arraybuffer');
            else if (ext === 'dbf') dbfBuffer = await f.async('arraybuffer');
            else if (ext === 'cpg') cpgBuffer = await f.async('arraybuffer');
            else if (ext === 'prj') prjText = await f.async('text');
          })
        );

        if (!shpBuffer) {
          reject(new Error('No .shp file found in the zip archive.'));
          return;
        }

        const parsedShp = shp.parseShp(shpBuffer, prjText ?? undefined);
        const parsedDbf = dbfBuffer
          ? shp.parseDbf(dbfBuffer, (cpgBuffer ?? dbfBuffer) as ArrayBuffer)
          : [];
        const geojson = shp.combine([parsedShp, parsedDbf]);

        resolve(geojson as GeoJsonPolygon);
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Error reading shapefile'));
      }
    };
    fileReader.onerror = (error) => reject(error);
    fileReader.readAsArrayBuffer(file);
  });
};

/**
 * Read KML file using OpenLayers KML parser.
 * Supports all valid KML 2.2 files (2D/3D coords, MultiGeometry, Folders, etc.)
 * @param file loaded KML file.
 * @returns Promise<GeoJsonPolygon>
 */
export const readKMLFile = (file: File) => {
  return new Promise<GeoJsonPolygon>((resolve, reject) => {
    if (file.name.split('.').pop() !== 'kml') {
      reject(new Error('Extension not allowed, please choose a .KML file.'));
      return;
    }

    const fileReader = new FileReader();

    fileReader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const text = e.target?.result as string;
        if (!text) {
          reject(new Error('error while parsing KML File.'));
          return;
        }

        const kmlFormat = new KML({ extractStyles: false });
        const geojsonFormat = new GeoJSON();

        // Parse KML into OpenLayers features
        const features = kmlFormat.readFeatures(text);

        // Find the first feature containing a Polygon or MultiPolygon geometry
        const polygonFeature = features.find((f: { getGeometry: () => { getType: () => string } | undefined }) => {
          const geomType = f.getGeometry()?.getType();
          return (
            geomType === 'Polygon' ||
            geomType === 'MultiPolygon' ||
            geomType === 'GeometryCollection'
          );
        });

        if (!polygonFeature || !polygonFeature.getGeometry()) {
          reject(new Error('No polygon tag was found in KML File.'));
          return;
        }

        // Convert the OL feature to GeoJSON
        const geojsonObject = JSON.parse(
          geojsonFormat.writeFeature(polygonFeature)
        );

        // Normalize geometry to Polygon (the application only supports single Polygon)
        let geometry = geojsonObject.geometry;
        if (geometry.type === 'MultiPolygon') {
          geometry = {
            type: 'Polygon',
            coordinates: geometry.coordinates[0],
          };
        } else if (geometry.type === 'GeometryCollection') {
          const polygonGeom = geometry.geometries.find(
            (g: { type: string }) => g.type === 'Polygon' || g.type === 'MultiPolygon'
          );
          if (polygonGeom) {
            geometry = polygonGeom.type === 'MultiPolygon'
              ? { type: 'Polygon', coordinates: polygonGeom.coordinates[0] }
              : polygonGeom;
          }
        }

        const geoJson = new GeoJsonPolygon();
        geoJson.features[0].geometry = geometry;
        resolve(geoJson);
      } catch {
        reject(new Error('error while parsing KML File.'));
      }
    };

    fileReader.onerror = (error) => reject(error);
    fileReader.readAsText(file);
  });
};
